import React, { useMemo } from 'react';
import { EdgeProps, BaseEdge, Position, useStore, ReactFlowState } from 'reactflow';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { Point, Rect, longestSegmentMidpoint, routeEdge, roundedOrthogonalPath } from './routing';
import { isEdgeRoutingEnabled } from './edgeRoutingFlag';
import { legacyEdgePath } from '../../../edit/legacyEdgePath';
import type { FlowEdgeVariant } from '../../../edit/flowEdgeHelpers';
import './RoutedEdge.scss';

// Unified obstacle-avoiding edge (issue #918). One renderer for every edge type:
// direction is derived live from geometry, and the look comes from the `variant`, which
// `flowEdgeTypes` fixes per react-flow type key (react-flow does not pass `type` to the
// renderer). The persisted `edge.type` is what the backend scheduler acts on.
//
// A runtime flag can disable routing (see edgeRoutingFlag); disabled edges fall back to a
// plain smoothstep, or a loop arc for backward edges — cheap and non-routed.

const dirFor = (pos?: Position): Point => {
  switch (pos) {
    case Position.Left:
      return { x: -1, y: 0 };
    case Position.Top:
      return { x: 0, y: -1 };
    case Position.Bottom:
      return { x: 0, y: 1 };
    case Position.Right:
    default:
      return { x: 1, y: 0 };
  }
};

// Node rectangles to route around: every rendered node with a measured size, minus the
// ancestor group boxes that contain either endpoint (a subflow container must not block
// its own children's edges). The endpoints' own leaf boxes stay in — the router pins the
// route to the handles and routes around everything else.
const buildObstacles = (internals: ReactFlowState['nodeInternals'], sourceId: string, targetId: string): Rect[] => {
  const ancestorsOf = (id: string): string[] => {
    const chain: string[] = [];
    let node = internals.get(id);
    while (node?.parentNode) {
      chain.push(node.parentNode);
      node = internals.get(node.parentNode);
    }
    return chain;
  };
  const skip = new Set([...ancestorsOf(sourceId), ...ancestorsOf(targetId)]);
  const rects: Rect[] = [];
  internals.forEach((node) => {
    if (skip.has(node.id) || node.hidden) return;
    const pos = node.positionAbsolute;
    if (!pos || !node.width || !node.height) return;
    rects.push({ x: pos.x, y: pos.y, width: node.width, height: node.height });
  });
  return rects;
};

// Legacy (routing-disabled) path uses legacyEdgePath (see legacyEdgePath.test.ts).

const RoutedEdge: React.FC<EdgeProps & { variant: FlowEdgeVariant }> = ({
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style,
  markerEnd,
  variant,
}) => {
  const routingEnabled = isEdgeRoutingEnabled();
  const nodeInternals = useStore((s: ReactFlowState) => s.nodeInternals);
  const isSelfLoop = source === target;

  // A defined waypoint set opts the edge out of live routing (`[]` = deliberately
  // straight). `undefined` → compute around obstacles now.
  const stored: Point[] | undefined = Array.isArray(data?.waypoints) ? data.waypoints : undefined;

  const waypoints = useMemo(() => {
    if (!routingEnabled) return [];
    if (stored) return stored;
    const obstacles = buildObstacles(nodeInternals, source, target);
    return routeEdge({
      source: { x: sourceX, y: sourceY },
      target: { x: targetX, y: targetY },
      sourceDir: dirFor(sourcePosition),
      targetDir: dirFor(targetPosition),
      obstacles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routingEnabled, stored, nodeInternals, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition]);

  let path: string;
  let mid: Point;
  if (!routingEnabled) {
    ({ path, mid } = legacyEdgePath(sourceX, sourceY, targetX, targetY, isSelfLoop));
  } else {
    const full: [Point, ...Point[]] = [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }];
    path = roundedOrthogonalPath(full);
    mid = longestSegmentMidpoint(full);
  }

  return (
    <g className={`flow-edge flow-edge--${variant}`}>
      <BaseEdge path={path} style={style} markerEnd={markerEnd} />
      {variant === 'loopback' && <LoopbackBadge at={mid} />}
      {variant === 'error-loopback' && <ErrorLoopbackBadge at={mid} />}
    </g>
  );
};

const BADGE_RADIUS = 10;
const [ROTATE_WIDTH, ROTATE_HEIGHT, , , ROTATE_PATH] = faRotateLeft.icon;
const ROTATE_SIZE = 11;

const LoopbackBadge = ({ at }: { at: Point }) => (
  <g className="flow-edge__badge" pointerEvents="none">
    <circle cx={at.x} cy={at.y} r={BADGE_RADIUS} />
    <path
      transform={`translate(${at.x - ROTATE_SIZE / 2} ${at.y - ROTATE_SIZE / 2}) scale(${ROTATE_SIZE / Math.max(ROTATE_WIDTH, ROTATE_HEIGHT)})`}
      d={typeof ROTATE_PATH === 'string' ? ROTATE_PATH : ROTATE_PATH.join(' ')}
    />
  </g>
);

const ErrorLoopbackBadge = ({ at }: { at: Point }) => (
  <g className="flow-edge__badge" pointerEvents="none">
    <circle cx={at.x} cy={at.y} r={BADGE_RADIUS} />
    <text x={at.x} y={at.y + 4} textAnchor="middle" fontSize={10} fontWeight={700}>
      !
    </text>
  </g>
);

const DefaultEdge = (props: EdgeProps) => <RoutedEdge {...props} variant="default" />;
const LoopbackEdge = (props: EdgeProps) => <RoutedEdge {...props} variant="loopback" />;
const ErrorLoopbackEdge = (props: EdgeProps) => <RoutedEdge {...props} variant="error-loopback" />;

/** react-flow `edgeTypes`. Module-level so its identity is stable; canvas edges are typed by `coerceEdgeTypeForCanvas`. */
export const flowEdgeTypes: Record<FlowEdgeVariant, React.FC<EdgeProps>> = {
  default: DefaultEdge,
  loopback: LoopbackEdge,
  'error-loopback': ErrorLoopbackEdge,
};
